import { computed, defineComponent, onBeforeUnmount, onMounted, ref, } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { Button } from '@yellowcard/components-library-vue3';
import LoaderComponent from '@/components/general/LoaderComponent.vue';
import WidgetApiService, { fiatSettlementErrorReason, } from '@/common/services/widget-api.service';
import { useAuthStore } from '@/store/modules/auth';
import { WidgetApiServiceTypes } from '@/types/widgetApiService';
import { numberFormat } from '@/utils/helpers';
var Status = WidgetApiServiceTypes.Status;
var FiatSettlementErrorReason = WidgetApiServiceTypes.FiatSettlementErrorReason;
const images = {
    success: require('@/assets/circleCheckIcon.svg'),
    chevron: require('@/assets/navigation/chevron_down.svg'),
    clock: require('@/assets/navigation/cogsClock.svg'),
    notFound: require('@/assets/notFound2.svg'),
    declined: require('@/assets/blocked.svg'),
};
// Every non-pending transaction lands on one of these, so a fetch and an
// authorize render the same screen. A refresh after authorizing reads as
// success, not as an error.
var Outcome;
(function (Outcome) {
    Outcome["AUTHORIZED"] = "authorized";
    Outcome["PROCESSING"] = "processing";
    Outcome["COMPLETE"] = "complete";
    Outcome["EXPIRED"] = "expired";
    Outcome["DECLINED"] = "declined";
    Outcome["FAILED"] = "failed";
    Outcome["NOT_FOUND"] = "notFound";
    Outcome["INVALID_STATE"] = "invalidState";
    Outcome["UNKNOWN"] = "unknown";
})(Outcome || (Outcome = {}));
const OUTCOME_COPY = {
    [Outcome.AUTHORIZED]: {
        heading: 'Transaction authorized',
        description: 'This transaction has been sent for processing. You can close this window.',
        success: true,
    },
    [Outcome.PROCESSING]: {
        heading: 'This transaction is authorized',
        description: 'It has been sent for processing, so nothing further is needed here.',
        success: true,
    },
    [Outcome.COMPLETE]: {
        heading: 'This transaction is complete',
        description: 'The funds have been sent, so nothing further is needed here.',
        success: true,
    },
    [Outcome.EXPIRED]: {
        heading: 'This transaction has expired',
        description: 'It can no longer be authorized. Please start a new transaction.',
        image: images.clock,
    },
    [Outcome.DECLINED]: {
        heading: 'This transaction was declined',
        description: 'It can no longer be authorized. Please start a new transaction.',
        image: images.declined,
    },
    [Outcome.FAILED]: {
        heading: "This transaction didn't go through",
        description: 'It can no longer be authorized. Please start a new transaction.',
        image: images.clock,
    },
    [Outcome.NOT_FOUND]: {
        heading: "We can't find this transaction",
        description: 'This transaction does not exist or is no longer available. Please go back and try again.',
        image: images.notFound,
    },
    [Outcome.INVALID_STATE]: {
        heading: 'This transaction is no longer pending',
        description: 'It has already been authorized or declined, so nothing further is needed.',
        image: images.clock,
    },
    [Outcome.UNKNOWN]: {
        heading: "We're sorry about this",
        description: "We're not sure what went wrong. Please try again in a few minutes.",
        image: images.clock,
    },
};
const OUTCOME_FOR_ERROR = {
    [FiatSettlementErrorReason.NOT_FOUND]: Outcome.NOT_FOUND,
    [FiatSettlementErrorReason.EXPIRED]: Outcome.EXPIRED,
    [FiatSettlementErrorReason.INVALID_STATE]: Outcome.INVALID_STATE,
    [FiatSettlementErrorReason.UNKNOWN]: Outcome.UNKNOWN,
};
// Null = still ours to authorize.
function outcomeForStatus(status) {
    switch (status) {
        case Status.PENDING_APPROVAL:
            return null;
        case Status.PROCESS:
        case Status.PROCESSING:
        case Status.PENDING:
        case Status.PENDING_SETTLEMENT:
        case Status.SETTLEMENT_PENDING:
        case Status.SETTLEMENT_PROCESSING:
            return Outcome.PROCESSING;
        case Status.COMPLETE:
        case Status.SETTLEMENT_COMPLETE:
            return Outcome.COMPLETE;
        case Status.EXPIRED:
            return Outcome.EXPIRED;
        case Status.DENIED:
        case Status.CANCELLED:
            return Outcome.DECLINED;
        case Status.FAILED:
        case Status.SETTLEMENT_FAILED:
        case Status.REFUNDED:
        case Status.PENDING_REFUND:
        case Status.REFUND_PROCESSING:
        case Status.REFUND_FAILED:
            return Outcome.FAILED;
        default:
            return Outcome.INVALID_STATE;
    }
}
const EMPTY_LEG = {
    amount: 0,
    convertedAmount: 0,
    currency: '',
    country: '',
    rate: 0,
    status: Status.PENDING_APPROVAL,
    fees: {},
    account: {},
};
// A missing icon is fine, so the lookup fails quietly.
function currencyIcon(currency) {
    if (!currency)
        return '';
    try {
        return require(`@/assets/currencies/${currency.toUpperCase()}.svg`);
    }
    catch (e) {
        return '';
    }
}
// ~4 significant digits, so 1,512.09 and 0.0956 both read cleanly.
function rateDecimals(rate) {
    const magnitude = Math.floor(Math.log10(Math.abs(rate)));
    return Math.min(8, Math.max(2, 3 - magnitude));
}
function countdown(seconds) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes}:${(seconds % 60).toString().padStart(2, '0')}`;
}
export default defineComponent({
    name: 'FiatSettlementView',
    components: {
        Button,
        LoaderComponent,
    },
    setup() {
        const route = useRoute();
        const authStore = useAuthStore();
        const { partnerSessionId, deviceId, partnerRedirectEnabled, partnerRedirectLink, } = storeToRefs(authStore);
        const txId = computed(() => route.query.txId?.toString() ?? '');
        const settlement = ref(null);
        const status = ref(null);
        const outcome = ref(null);
        const submitError = ref('');
        const loading = ref(true);
        const authorizing = ref(false);
        const secondsLeft = ref(null);
        const receive = computed(() => settlement.value?.receive ?? EMPTY_LEG);
        const send = computed(() => settlement.value?.send ?? EMPTY_LEG);
        // Only pending_approval, and only while its window is still open.
        const canAuthorize = computed(() => !!settlement.value &&
            status.value === Status.PENDING_APPROVAL &&
            !outcome.value);
        const outcomeCopy = computed(() => outcome.value ? OUTCOME_COPY[outcome.value] : null);
        const receiveIcon = computed(() => currencyIcon(receive.value.currency));
        const sendIcon = computed(() => currencyIcon(send.value.currency));
        const formatMoney = (amount, currency) => `${numberFormat(amount, 2, 2)} ${currency}`.trim();
        // Each leg is priced per USD, so the cross rate is send.rate / receive.rate.
        const rateLabel = computed(() => {
            const { rate: from } = receive.value;
            const { rate: to } = send.value;
            if (!from || !to)
                return '—';
            const rate = to / from;
            return `1 ${receive.value.currency} = ${numberFormat(rate, rateDecimals(rate), 2)} ${send.value.currency}`;
        });
        // Collection leg only - the pre-created payment leg carries no fees.
        const totalFees = computed(() => {
            const { serviceFeeAmountLocal, networkFeeAmountLocal, partnerFeeAmountLocal, } = receive.value.fees;
            return ((serviceFeeAmountLocal ?? 0) +
                (networkFeeAmountLocal ?? 0) +
                (partnerFeeAmountLocal ?? 0));
        });
        // Name on top, bank and number underneath.
        const accountFor = (leg) => {
            const { accountName, accountBank, accountNumber } = leg.account;
            const detail = [accountBank, accountNumber].filter(Boolean).join(' • ');
            const name = accountName || detail;
            if (!name)
                return null;
            return { name, detail: detail === name ? '' : detail };
        };
        const source = computed(() => accountFor(receive.value));
        const destination = computed(() => accountFor(send.value));
        const referenceRows = computed(() => {
            const rows = [];
            if (settlement.value) {
                rows.push({
                    label: 'Amount',
                    value: `${formatMoney(receive.value.convertedAmount, receive.value.currency)} → ${formatMoney(send.value.convertedAmount, send.value.currency)}`,
                });
            }
            if (txId.value) {
                rows.push({ label: 'Transaction ID', value: txId.value });
            }
            return rows;
        });
        // The window is ten minutes, short enough that the user needs to see it.
        // The server stays authoritative; this only stops a click already too late.
        let expiryTimer;
        const remainingSeconds = () => {
            const { expiresAt } = settlement.value ?? {};
            if (!expiresAt)
                return null;
            const ms = new Date(expiresAt).getTime() - Date.now();
            if (Number.isNaN(ms))
                return null;
            return Math.max(0, Math.round(ms / 1000));
        };
        const stopExpiryCountdown = () => {
            if (expiryTimer)
                clearInterval(expiryTimer);
            expiryTimer = undefined;
        };
        const startExpiryCountdown = () => {
            secondsLeft.value = remainingSeconds();
            if (secondsLeft.value === null)
                return;
            if (secondsLeft.value === 0) {
                outcome.value = Outcome.EXPIRED;
                return;
            }
            expiryTimer = window.setInterval(() => {
                secondsLeft.value = remainingSeconds();
                if (secondsLeft.value === 0) {
                    stopExpiryCountdown();
                    outcome.value = Outcome.EXPIRED;
                }
            }, 1000);
        };
        const expiresIn = computed(() => canAuthorize.value && secondsLeft.value
            ? countdown(secondsLeft.value)
            : '');
        const expiringSoon = computed(() => secondsLeft.value !== null && secondsLeft.value <= 120);
        const fetchSettlement = async () => {
            if (!txId.value) {
                outcome.value = Outcome.NOT_FOUND;
                loading.value = false;
                return;
            }
            try {
                const response = await WidgetApiService.GetFiatSettlement(txId.value, partnerSessionId.value, deviceId.value);
                settlement.value = response;
                status.value = response.status;
                outcome.value = outcomeForStatus(response.status);
                if (!outcome.value)
                    startExpiryCountdown();
            }
            catch (e) {
                outcome.value =
                    OUTCOME_FOR_ERROR[fiatSettlementErrorReason(e?.message)];
                console.error('failed to fetch fiat settlement:', e);
            }
            finally {
                loading.value = false;
            }
        };
        // Hands off to the partner, matching OrderSent.
        const sendEndFlowMessage = () => {
            // eslint-disable-next-line no-restricted-globals
            const targetWindow = window.opener || window.top;
            if (!targetWindow)
                return;
            targetWindow.postMessage(JSON.stringify({
                name: 'YCWidgetEndFlowEvent',
                transactionAuthorized: true,
                txId: txId.value,
                origin: `${window.location.protocol}//${window.location.host}`,
                redirectLink: partnerRedirectLink.value,
                redirectEnabled: partnerRedirectEnabled.value,
            }), '*');
        };
        const authorize = async () => {
            if (!canAuthorize.value || authorizing.value)
                return;
            submitError.value = '';
            authorizing.value = true;
            try {
                const response = await WidgetApiService.AcceptFiatSettlement(txId.value, partnerSessionId.value, deviceId.value);
                stopExpiryCountdown();
                status.value = response.status;
                outcome.value = Outcome.AUTHORIZED;
                if (partnerRedirectEnabled.value && partnerRedirectLink.value) {
                    window.location.href = partnerRedirectLink.value;
                }
                else {
                    sendEndFlowMessage();
                }
            }
            catch (e) {
                const reason = fiatSettlementErrorReason(e?.message);
                if (reason === FiatSettlementErrorReason.UNKNOWN) {
                    // nothing changed, so keep it on screen and let them retry
                    submitError.value =
                        "We couldn't authorize this transaction. Please try again.";
                }
                else {
                    stopExpiryCountdown();
                    outcome.value = OUTCOME_FOR_ERROR[reason];
                }
                console.error('failed to authorize fiat settlement:', e);
            }
            finally {
                authorizing.value = false;
            }
        };
        onMounted(fetchSettlement);
        onBeforeUnmount(stopExpiryCountdown);
        return {
            images,
            txId,
            loading,
            authorizing,
            outcomeCopy,
            receive,
            send,
            receiveIcon,
            sendIcon,
            canAuthorize,
            rateLabel,
            totalFees,
            source,
            destination,
            referenceRows,
            expiresIn,
            expiringSoon,
            submitError,
            numberFormat,
            formatMoney,
            authorize,
        };
    },
});
